import type { AppFeatures } from '@khaddi/contracts';
import { useApi, useAsync } from '@khaddi/ui';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

/** Used until /app-config answers, and if it never does: the in-store mode hides nothing a customer can lose. */
const FALLBACK: AppFeatures = { onlineOrdering: false, inStoreOrdering: false, delivery: false, onlinePayment: false, loyaltyRedeem: true, counterOrdering: false, phoneVerification: 'counter_code' };

type AppConfigValue = { features: AppFeatures; loaded: boolean };

const AppConfigContext = createContext<AppConfigValue | null>(null);

/** Store-wide switches from the console; refetched when the app returns to the foreground. */
export const AppConfigProvider = ({ children }: { children: ReactNode }) => {
  const api = useApi();
  const { data, reload } = useAsync(() => api.appConfig(), [api]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [reload]);

  const value = useMemo(() => ({ features: data?.features ?? FALLBACK, loaded: data !== null }), [data]);
  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
};

export const useAppConfig = (): AppConfigValue => {
  const value = useContext(AppConfigContext);
  if (!value) throw new Error('useAppConfig must be used inside <AppConfigProvider>');
  return value;
};

export const useFeatures = (): AppFeatures => useAppConfig().features;
